import apiClient from './client';

/**
 * Check diagnostico sul ledger (#116-bis).
 *
 * `totale_anomalie` arriva dal BACKEND e comprende i soli controlli 1 e 2:
 * NON va ricalcolato sommando le lunghezze qui, o contatore e pagina possono
 * divergere. I saldi negativi sono "da verificare", non anomalie, e restano
 * fuori dal totale per scelta.
 *
 * ATTENZIONE ai tipi numerici: sul backend questi campi sono `Decimal`, e
 * Pydantic li serializza in JSON come STRINGA (`"2E+4"` per 20.000, perche'
 * asyncpg restituisce i NUMERIC interi in forma esponenziale). Dichiararli
 * `number` e' stato il difetto che ha portato "2E+4 kg" a video: il tipo
 * mentiva, quindi nessuno vedeva che `fmtKg` stava girando su una stringa.
 * Passarli a `fmtKg`/`fmtPct`, che coercono; mai usarli in aritmetica diretta.
 */

/** Peso o percentuale dal ledger: number a runtime NON e' garantito. */
export type Peso = number | string;

export interface RigaInvariante {
  area_id: string;
  area_codice: string | null;
  materiale: string;
  /** null quando la riga di saldo non esiste affatto pur essendoci movimenti. */
  saldo_kg: Peso | null;
  somma_movimenti: Peso | null;
  scarto_kg: Peso;
}

export interface RigaCerDisallineato {
  conferimento_id: string;
  numero: string | null;
  codice_ordine: string | null;
  cer_ordine: string | null;
  area_id: string;
  area_codice: string | null;
  materiale: string;
  netto_kg: Peso;
}

export interface RigaSaldoNegativo {
  area_id: string;
  area_codice: string | null;
  materiale: string;
  saldo_kg: Peso;
}

/** `totale` è il conteggio PIENO: se `troncato`, `righe` ne mostra solo una parte. */
export interface Controllo<R> {
  totale: number;
  troncato: boolean;
  righe: R[];
}

/** Un'operazione di prelievo registrata per meno di quanto chiesto.
 *  `stato` separa cio' che richiede azione da cio' che e' solo informativo. */
export interface RigaScaricoRidotto {
  gruppo_id: string;
  stato: 'da_verificare' | 'sotto_soglia' | 'verificato';
  created_at: string;
  aree: string | null;
  materiali: string | null;
  peso_richiesto_kg: Peso;
  peso_registrato_kg: Peso;
  scarto_kg: Peso;
  scarto_pct: Peso;
  confermato_at: string | null;
  confermato_da_nome: string | null;
  verificato_at: string | null;
  verificato_da_nome: string | null;
}

export interface ControlloScarichiRidotti extends Controllo<RigaScaricoRidotto> {
  /** Solo gli `stato === 'da_verificare'`: e' il numero che entra nel contatore. */
  totale_da_verificare: number;
}

export interface DiagnosticaLedger {
  generato_at: string;
  totale_anomalie: number;
  invariante_saldi: Controllo<RigaInvariante>;
  cer_disallineato: Controllo<RigaCerDisallineato>;
  saldi_negativi: Controllo<RigaSaldoNegativo>;
  scarichi_ridotti: ControlloScarichiRidotti;
}

export async function getDiagnosticaLedger(): Promise<DiagnosticaLedger> {
  const { data } = await apiClient.get<DiagnosticaLedger>('/api/diagnostica/ledger');
  return data;
}

export interface PresaVisioneResponse {
  gruppo_id: string;
  verificato_at: string | null;
  verificato_da_nome: string | null;
}

/** Prende atto di una riduzione, o annulla la presa d'atto (`verificato: false`):
 *  un click sbagliato non deve nascondere un'anomalia per sempre. */
export async function setPresaVisione(gruppoId: string, verificato: boolean): Promise<PresaVisioneResponse> {
  const { data } = await apiClient.patch<PresaVisioneResponse>(
    `/api/diagnostica/gruppi/${gruppoId}/presa-visione`, { verificato });
  return data;
}
